import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { SagaIterator } from "redux-saga";
import { all, call, put, takeEvery, takeLeading } from "redux-saga/effects";
import { getAxiosErrorMessage } from "utils/getAxiosErrorMessage";
import { axiosInstance } from "../../axios";
import { loadBalancersActions } from "./slices";
import {
  AddPoolMemberParams,
  AddPoolMemberResponse,
  CreateHealthMonitorParams,
  CreateHealthMonitorResponse,
  CreateL7PolicyParams,
  CreateL7PolicyResponse,
  CreateL7RuleParams,
  CreateL7RuleResponse,
  CreateListenerParams,
  CreateListenerResponse,
  CreateLoadBalancerParams,
  CreateLoadBalancerResponse,
  CreatePoolParams,
  CreatePoolResponse,
  DeleteHealthMonitorParams,
  DeleteL7PolicyParams,
  DeleteL7RuleParams,
  DeleteListenerParams,
  DeleteLoadBalancerParams,
  DeletePoolParams,
  GetHealthMonitorDetailsParams,
  GetHealthMonitorDetailsResponse,
  GetHealthMonitorsListParams,
  GetHealthMonitorsListResponse,
  GetL7PoliciesListParams,
  GetL7PoliciesListResponse,
  GetL7PolicyDetailsParams,
  GetL7PolicyDetailsResponse,
  GetL7RuleDetailsParams,
  GetL7RuleDetailsResponse,
  GetL7RulesListParams,
  GetL7RulesListResponse,
  GetListenerDetailsParams,
  GetListenerDetailsResponse,
  GetListenersListParams,
  GetListenersListResponse,
  GetLoadBalancerDetailsParams,
  GetLoadBalancerDetailsResponse,
  GetLoadBalancersListParams,
  GetLoadBalancersListResponse,
  GetPoolDetailsParams,
  GetPoolDetailsResponse,
  GetPoolMemberDetailsParams,
  GetPoolMemberDetailsResponse,
  GetPoolMembersListParams,
  GetPoolMembersListResponse,
  GetPoolsListParams,
  GetPoolsListResponse,
  RemovePoolMemberParams,
  UpdateHealthMonitorParams,
  UpdateHealthMonitorResponse,
  UpdateL7PolicyParams,
  UpdateL7PolicyResponse,
  UpdateL7RuleParams,
  UpdateL7RuleResponse,
  UpdateListenerParams,
  UpdateListenerResponse,
  UpdateLoadBalancerParams,
  UpdateLoadBalancerResponse,
  UpdatePoolMemberParams,
  UpdatePoolMemberResponse,
  UpdatePoolParams,
  UpdatePoolResponse
} from "./types";

// LOAD_BALANCERS _________________________________________________________________________________

export function* listLoadBalancersSaga(
  action: PayloadAction<GetLoadBalancersListParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetLoadBalancersListResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.listLoadBalancersSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.listLoadBalancersFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get Load Balancers data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getLoadBalancerSaga(
  action: PayloadAction<GetLoadBalancerDetailsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, lbId } = action.payload;
    const response: AxiosResponse<GetLoadBalancerDetailsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.getLoadBalancerSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.getLoadBalancerFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get Load Balancer details data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createLoadBalancerSaga(
  action: PayloadAction<CreateLoadBalancerParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the Load Balancer...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateLoadBalancerResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers`,
      data
    );

    yield put(loadBalancersActions.createLoadBalancerSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Load Balancer has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.createLoadBalancerFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create Load Balancer ",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateLoadBalancerSaga(
  action: PayloadAction<UpdateLoadBalancerParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the Load Balancer...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, lbId, data } = action.payload;
    const response: AxiosResponse<UpdateLoadBalancerResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}`,
      data
    );
    yield put(loadBalancersActions.updateLoadBalancerSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Load Balancer has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.updateLoadBalancerFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update Load Balancer",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteLoadBalancerSaga(
  action: PayloadAction<DeleteLoadBalancerParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the Load Balancer...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, lbId } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}`
    );
    yield put(loadBalancersActions.deleteLoadBalancerSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Load Balancer has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.deleteLoadBalancerFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete Load Balancer",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

// LISTENERS _________________________________________________________________________________

export function* listListenersSaga(
  action: PayloadAction<GetListenersListParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, lbId } = action.payload;
    const response: AxiosResponse<GetListenersListResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}/listeners`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.listListenersSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.listListenersFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get Listeners data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getListenerSaga(
  action: PayloadAction<GetListenerDetailsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, lbId, listenerId } = action.payload;
    const response: AxiosResponse<GetListenerDetailsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}/listeners/${listenerId}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.getListenerSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.getListenerFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get Listener details data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createListenerSaga(
  action: PayloadAction<CreateListenerParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the Listener...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, lbId, data } = action.payload;
    const response: AxiosResponse<CreateListenerResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}/listeners`,
      data
    );

    yield put(loadBalancersActions.createListenerSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Listener has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.createListenerFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create Listener ",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateListenerSaga(
  action: PayloadAction<UpdateListenerParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the Listener...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, lbId, listenerId, data } = action.payload;
    const response: AxiosResponse<UpdateListenerResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}/listeners/${listenerId}`,
      data
    );
    yield put(loadBalancersActions.updateListenerSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Listener has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.updateListenerFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update Listener",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteListenerSaga(
  action: PayloadAction<DeleteListenerParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the Listener...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, lbId, listenerId } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}/listeners/${listenerId}`
    );
    yield put(loadBalancersActions.deleteListenerSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Listener has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.deleteListenerFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete Listener",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

// POOLS _________________________________________________________________________________

export function* listPoolsSaga(
  action: PayloadAction<GetPoolsListParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, lbId } = action.payload;
    const response: AxiosResponse<GetPoolsListResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}/pools`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.listPoolsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.listPoolsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get Pools data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getPoolSaga(
  action: PayloadAction<GetPoolDetailsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, lbId, poolId } = action.payload;
    const response: AxiosResponse<GetPoolDetailsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}/pools/${poolId}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.getPoolSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.getPoolFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get Pool details data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createPoolSaga(
  action: PayloadAction<CreatePoolParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the Pool...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, lbId, data } = action.payload;
    const response: AxiosResponse<CreatePoolResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}/pools`,
      data
    );

    yield put(loadBalancersActions.createPoolSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Pool has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.createPoolFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create Pool ",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updatePoolSaga(
  action: PayloadAction<UpdatePoolParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the Pool ...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, lbId, poolId, data } = action.payload;
    const response: AxiosResponse<UpdatePoolResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}/pools/${poolId}`,
      data
    );
    yield put(loadBalancersActions.updatePoolSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Pool has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.updatePoolFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update Pool",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deletePoolSaga(
  action: PayloadAction<DeletePoolParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the Pool...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, lbId, poolId } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-load-balancer/method/${projectId}/loadbalancers/${lbId}/pools/${poolId}`
    );
    yield put(loadBalancersActions.deletePoolSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Pool has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.deletePoolFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete Pool",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

// POOL MEMBERS _________________________________________________________________________________

export function* listPoolMembersSaga(
  action: PayloadAction<GetPoolMembersListParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, poolId } = action.payload;
    const response: AxiosResponse<GetPoolMembersListResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/pools/${poolId}/members`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.listPoolMembersSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.listPoolMembersFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get Pool Members data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getPoolMemberSaga(
  action: PayloadAction<GetPoolMemberDetailsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, poolId, memberId } = action.payload;
    const response: AxiosResponse<GetPoolMemberDetailsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/pools/${poolId}/members/${memberId}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.getPoolMemberSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.getPoolMemberFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get Pool Member details data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* addPoolMemberSaga(
  action: PayloadAction<AddPoolMemberParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Adding the Pool Member...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, poolId, data } = action.payload;
    const response: AxiosResponse<AddPoolMemberResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-load-balancer/method/${projectId}/pools/${poolId}/members`,
      data
    );

    yield put(loadBalancersActions.addPoolMemberSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Pool Member has been successfully added.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.addPoolMemberFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to add Pool Member ",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updatePoolMemberSaga(
  action: PayloadAction<UpdatePoolMemberParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the Pool Member ...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, poolId, memberId, data } = action.payload;
    const response: AxiosResponse<UpdatePoolMemberResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-load-balancer/method/${projectId}/pools/${poolId}/members/${memberId}`,
      data
    );
    yield put(loadBalancersActions.updatePoolMemberSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Pool Member has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.updatePoolMemberFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update Pool Member",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* removePoolMemberSaga(
  action: PayloadAction<RemovePoolMemberParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Removing the Pool Member...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, poolId, memberId } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-load-balancer/method/${projectId}/pools/${poolId}/members/${memberId}`
    );
    yield put(loadBalancersActions.removePoolMemberSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Pool Member has been successfully removed.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.removePoolMemberFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to remove Pool Member",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

// L7 POLICIES _________________________________________________________________________________

export function* listL7PoliciesSaga(
  action: PayloadAction<GetL7PoliciesListParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, listenerId } = action.payload;
    const response: AxiosResponse<GetL7PoliciesListResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/listeners/${listenerId}/l7policies`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.listL7PoliciesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.listL7PoliciesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get L7 Policies data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getL7PolicySaga(
  action: PayloadAction<GetL7PolicyDetailsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, listenerId, l7PolicyId } = action.payload;
    const response: AxiosResponse<GetL7PolicyDetailsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/listeners/${listenerId}/l7policies/${l7PolicyId}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.getL7PolicySuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.getL7PolicyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get L7 Policy details data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createL7PolicySaga(
  action: PayloadAction<CreateL7PolicyParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the L7 Policy...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, listenerId, data } = action.payload;
    const response: AxiosResponse<CreateL7PolicyResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-load-balancer/method/${projectId}/listeners/${listenerId}/l7policies/`,
      data
    );

    yield put(loadBalancersActions.createL7PolicySuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "L7 Policy has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.createL7PolicyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create L7 Policy ",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateL7PolicySaga(
  action: PayloadAction<UpdateL7PolicyParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the L7 Policy ...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, listenerId, l7PolicyId, data } =
      action.payload;
    const response: AxiosResponse<UpdateL7PolicyResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-load-balancer/method/${projectId}/listeners/${listenerId}/l7policies/${l7PolicyId}`,
      data
    );
    yield put(loadBalancersActions.updateL7PolicySuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "L7 Policy has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.updateL7PolicyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update L7 Policy",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteL7PolicySaga(
  action: PayloadAction<DeleteL7PolicyParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the L7 Policy...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, listenerId, l7PolicyId } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-load-balancer/method/${projectId}/listeners/${listenerId}/l7policies/${l7PolicyId}`
    );
    yield put(loadBalancersActions.deleteL7PolicySuccess());
    yield put(
      notificationsActions.showNotification({
        title: "L7 Policy has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.deleteL7PolicyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete L7 Policy",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

// L7 POLICY RULES _________________________________________________________________________________

export function* listL7PolicyRulesSaga(
  action: PayloadAction<GetL7RulesListParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, l7PolicyId } = action.payload;
    const response: AxiosResponse<GetL7RulesListResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/l7policies/${l7PolicyId}/rules`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.listL7PolicyRulesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.listL7PolicyRulesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get L7 Policy Rules data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getL7PolicyRuleSaga(
  action: PayloadAction<GetL7RuleDetailsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, l7PolicyId, ruleId } = action.payload;
    const response: AxiosResponse<GetL7RuleDetailsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/l7policies/${l7PolicyId}/rules/${ruleId}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.getL7PolicyRuleSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.getL7PolicyRuleFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get L7 Policy Rule details data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createL7PolicyRuleSaga(
  action: PayloadAction<CreateL7RuleParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the L7 Policy Rule...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, l7PolicyId, data } = action.payload;
    const response: AxiosResponse<CreateL7RuleResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-load-balancer/method/${projectId}/l7policies/${l7PolicyId}/rules`,
      data
    );

    yield put(loadBalancersActions.createL7PolicyRuleSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "L7 Policy Rule has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.createL7PolicyRuleFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create L7 Policy Rule",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateL7PolicyRuleSaga(
  action: PayloadAction<UpdateL7RuleParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the L7 Policy Rule...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, l7PolicyId, ruleId, data } = action.payload;
    const response: AxiosResponse<UpdateL7RuleResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-load-balancer/method/${projectId}/l7policies/${l7PolicyId}/rules/${ruleId}`,
      data
    );
    yield put(loadBalancersActions.updateL7PolicyRuleSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "L7 Policy Rule has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.updateL7PolicyRuleFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update L7 Policy Rule",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteL7PolicyRuleSaga(
  action: PayloadAction<DeleteL7RuleParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the L7 Policy Rule...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, l7PolicyId, ruleId } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-load-balancer/method/${projectId}/l7policies/${l7PolicyId}/rules/${ruleId}`
    );
    yield put(loadBalancersActions.deleteL7PolicyRuleSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "L7 Policy Rule has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.deleteL7PolicyRuleFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete L7 Policy Rule",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

// HEALTH MONITORS _________________________________________________________________________________

export function* listHealthMonitorsSaga(
  action: PayloadAction<GetHealthMonitorsListParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, poolId } = action.payload;
    const response: AxiosResponse<GetHealthMonitorsListResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/pools/${poolId}/monitors`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.listHealthMonitorsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.listHealthMonitorsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get Health Monitors data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getHealthMonitorSaga(
  action: PayloadAction<GetHealthMonitorDetailsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, poolId, monitorId } = action.payload;
    const response: AxiosResponse<GetHealthMonitorDetailsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-load-balancer/method/${projectId}/pools/${poolId}/monitors/${monitorId}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(loadBalancersActions.getHealthMonitorSuccess(response.data));
    }
  } catch (e) {
    yield put(
      loadBalancersActions.getHealthMonitorFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get Health Monitor details data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createHealthMonitorSaga(
  action: PayloadAction<CreateHealthMonitorParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the Health Monitor...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, poolId, data } = action.payload;
    const response: AxiosResponse<CreateHealthMonitorResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-load-balancer/method/${projectId}/pools/${poolId}/monitors`,
      data
    );

    yield put(loadBalancersActions.createHealthMonitorSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Health Monitor has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.createHealthMonitorFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create Health Monitor",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateHealthMonitorSaga(
  action: PayloadAction<UpdateHealthMonitorParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the Health Monitor...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, poolId, monitorId, data } = action.payload;
    const response: AxiosResponse<UpdateHealthMonitorResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-load-balancer/method/${projectId}/pools/${poolId}/monitors/${monitorId}`,
      data
    );
    yield put(loadBalancersActions.updateHealthMonitorSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Health Monitor has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.updateHealthMonitorFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update Health Monitor",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteHealthMonitorSaga(
  action: PayloadAction<DeleteHealthMonitorParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the Health Monitor...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, poolId, monitorId } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-load-balancer/method/${projectId}/pools/${poolId}/monitors/${monitorId}`
    );
    yield put(loadBalancersActions.deleteHealthMonitorSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Health Monitor has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      loadBalancersActions.deleteHealthMonitorFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete Health Monitor",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([
    takeLeading(
      loadBalancersActions.listLoadBalancersRequest,
      listLoadBalancersSaga
    ),
    takeLeading(
      loadBalancersActions.getLoadBalancerRequest,
      getLoadBalancerSaga
    ),
    takeEvery(
      loadBalancersActions.createLoadBalancerRequest,
      createLoadBalancerSaga
    ),
    takeEvery(
      loadBalancersActions.updateLoadBalancerRequest,
      updateLoadBalancerSaga
    ),
    takeEvery(
      loadBalancersActions.deleteLoadBalancerRequest,
      deleteLoadBalancerSaga
    ),

    takeLeading(loadBalancersActions.listListenersRequest, listListenersSaga),
    takeLeading(loadBalancersActions.getListenerRequest, getListenerSaga),
    takeEvery(loadBalancersActions.createListenerRequest, createListenerSaga),
    takeEvery(loadBalancersActions.updateListenerRequest, updateListenerSaga),
    takeEvery(loadBalancersActions.deleteListenerRequest, deleteListenerSaga),

    takeLeading(loadBalancersActions.listPoolsRequest, listPoolsSaga),
    takeLeading(loadBalancersActions.getPoolRequest, getPoolSaga),
    takeEvery(loadBalancersActions.createPoolRequest, createPoolSaga),
    takeEvery(loadBalancersActions.updatePoolRequest, updatePoolSaga),
    takeEvery(loadBalancersActions.deletePoolRequest, deletePoolSaga),

    takeLeading(
      loadBalancersActions.listPoolMembersRequest,
      listPoolMembersSaga
    ),
    takeLeading(loadBalancersActions.getPoolMemberRequest, getPoolMemberSaga),
    takeEvery(loadBalancersActions.addPoolMemberRequest, addPoolMemberSaga),
    takeEvery(
      loadBalancersActions.updatePoolMemberRequest,
      updatePoolMemberSaga
    ),
    takeEvery(
      loadBalancersActions.removePoolMemberRequest,
      removePoolMemberSaga
    ),

    takeLeading(loadBalancersActions.listL7PoliciesRequest, listL7PoliciesSaga),
    takeLeading(loadBalancersActions.getL7PolicyRequest, getL7PolicySaga),
    takeEvery(loadBalancersActions.createL7PolicyRequest, createL7PolicySaga),
    takeEvery(loadBalancersActions.updateL7PolicyRequest, updateL7PolicySaga),
    takeEvery(loadBalancersActions.deleteL7PolicyRequest, deleteL7PolicySaga),

    takeLeading(
      loadBalancersActions.listL7PolicyRulesRequest,
      listL7PolicyRulesSaga
    ),
    takeLeading(
      loadBalancersActions.getL7PolicyRuleRequest,
      getL7PolicyRuleSaga
    ),
    takeEvery(
      loadBalancersActions.createL7PolicyRuleRequest,
      createL7PolicyRuleSaga
    ),
    takeEvery(
      loadBalancersActions.updateL7PolicyRuleRequest,
      updateL7PolicyRuleSaga
    ),
    takeEvery(
      loadBalancersActions.deleteL7PolicyRuleRequest,
      deleteL7PolicyRuleSaga
    ),

    takeLeading(
      loadBalancersActions.listHealthMonitorsRequest,
      listHealthMonitorsSaga
    ),
    takeLeading(
      loadBalancersActions.getHealthMonitorRequest,
      getHealthMonitorSaga
    ),
    takeEvery(
      loadBalancersActions.createHealthMonitorRequest,
      createHealthMonitorSaga
    ),
    takeEvery(
      loadBalancersActions.updateHealthMonitorRequest,
      updateHealthMonitorSaga
    ),
    takeEvery(
      loadBalancersActions.deleteHealthMonitorRequest,
      deleteHealthMonitorSaga
    )
  ]);
}
